'use client';

import { Popover, PopoverAnchor, PopoverContent } from '@heidi/design-system/popover';
import { ModalId } from '@heidi/schema/ml-scribe';
import { cn } from '@heidi/utils';
import { type ReactElement, useEffect } from 'react';
import { useSessionMergeOnboardingAnalytics } from '../../../../data/analytics/useSessionMergeOnboardingAnalytics';
import { useEnableSessionMerge } from '../../hooks/useEnableSessionMerge';
import { useSessionMergeOnboardingUiHost } from '../../host';
import { EnableSessionMergeCard } from './enable-session-merge-card';

export type EnableSessionMergeCardPopoverProps = {
  /**
   * Queue id this popover is bound to. Defaults to the Home card; the settings
   * second-chance card passes `ModalId.SESSION_MERGE_ONBOARDING_SETTINGS` so it
   * tracks a separate seen-state.
   */
  modalId?: ModalId;
};

/**
 * Card owns its surface chrome, so strip the PopoverContent defaults (incl. the
 * experimental ring/shadow). Mirrors `SessionlessEvidenceAwarenessPopover`.
 */
const floatingPopoverContentClassName = cn(
  'z-[60] w-[min(calc(100vw-2rem),15rem)] max-w-60',
  '!rounded-none !border-0 !bg-transparent !p-0 !shadow-none !ring-0',
  'data-[experimental=true]:!gap-0 data-[experimental=true]:!rounded-none',
  'data-[experimental=true]:!border-0 data-[experimental=true]:!shadow-none',
  'data-[experimental=true]:!ring-0'
);

/**
 * Floating, queue-gated awareness popover for the one-time "Enable Session
 * Merge" onboarding card. Visibility is owned by the modal queue
 * (`ModalId.SESSION_MERGE_ONBOARDING`); `seen_modals` keeps it to once per user
 * across devices. Mirrors `SessionlessEvidenceAwarenessPopover` for the floating
 * shell and `PatientListIntroductionModal` for the queue wiring.
 *
 * "Try now" enables auto session merge (and shows the success toast) via
 * `useEnableSessionMerge`, then closes; the close (X / Escape) dismisses without
 * enabling. Both paths mark the modal seen through `handleClose`.
 */
export function EnableSessionMergeCardPopover({
  modalId = ModalId.SESSION_MERGE_ONBOARDING,
}: EnableSessionMergeCardPopoverProps = {}): ReactElement {
  const { useQueuedModal } = useSessionMergeOnboardingUiHost();
  const { isOpen, handleClose } = useQueuedModal(modalId);
  const { enableSessionMerge, isEnabling } = useEnableSessionMerge();
  const { trackViewed, trackAccepted, trackDismissed } =
    useSessionMergeOnboardingAnalytics();

  // `session_merge_onboarding_viewed` — fire once the queue surfaces the promo.
  useEffect(() => {
    if (isOpen) trackViewed();
  }, [isOpen, trackViewed]);

  // Dismiss without enabling (X / escape). Kept separate from `handleTryNow` so
  // the accept path never also counts as a dismissal.
  const handleDismiss = () => {
    trackDismissed();
    handleClose();
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) handleDismiss();
  };

  const handleTryNow = () => {
    trackAccepted();
    enableSessionMerge();
    handleClose();
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange} modal={false}>
      <PopoverAnchor
        className="pointer-events-none absolute bottom-3.5 start-2 z-[55] size-px"
        aria-hidden
      />
      <PopoverContent
        side="right"
        align="end"
        onInteractOutside={(event) => event.preventDefault()}
        className={floatingPopoverContentClassName}
      >
        <EnableSessionMergeCard
          onTryNow={handleTryNow}
          onDismiss={handleDismiss}
          isEnabling={isEnabling}
        />
      </PopoverContent>
    </Popover>
  );
}
